import { Button } from "@bednbody/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bednbody/ui/components/dropdown-menu";
import {
  BriefcaseIcon,
  CircleIcon,
  DotsThreeVerticalIcon,
  IdentificationCardIcon,
  KeyIcon,
  MedalIcon,
  UserIcon,
  UserListIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import type { RiderVehicle } from "../../staff-media/staff-media.hooks";
import type { StaffAccess } from "../staff-detail/staff-detail.access";
import { StaffEditDialogs, type StaffEditKind } from "./StaffEditDialogs";
import type { RosterPerson } from "./types";

type RosterRowActionsProps = {
  person: RosterPerson;
  /** From `staffDetailAccess` — the detail modal reads the same flags. */
  access: StaffAccess;
  onViewDetails: () => void;
  /** The rider's plate on record, if any — riders only (0279). */
  vehicle?: RiderVehicle | null;
};

export function RosterRowActions({
  person,
  access,
  onViewDetails,
  vehicle = null,
}: RosterRowActionsProps) {
  const [dialog, setDialog] = useState<StaffEditKind | null>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size="icon" aria-label={`Actions for ${person.name}`} />}
        >
          <DotsThreeVerticalIcon weight="bold" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onViewDetails}>
            <UserListIcon weight="fill" />
            View details
          </DropdownMenuItem>
          {access.canToggleActive ||
          access.canChangeRole ||
          access.canSetRiderRank ||
          access.canSetTherapistRank ||
          access.canSetPlate ||
          access.canReassignHub ||
          access.canResetPassword ? (
            <DropdownMenuSeparator />
          ) : null}
          {access.canToggleActive ? (
            <DropdownMenuItem onClick={() => setDialog("toggle")}>
              {person.active ? <XCircleIcon weight="fill" /> : <CircleIcon weight="fill" />}
              Set {person.active ? "Inactive" : "Active"}
            </DropdownMenuItem>
          ) : null}
          {access.canChangeRole ? (
            <DropdownMenuItem onClick={() => setDialog("role")}>
              <UserIcon weight="fill" />
              Change Role
            </DropdownMenuItem>
          ) : null}
          {/* RIDER-031: rank decides Diamond Session priority, which is supply,
              not a personnel setting. */}
          {access.canSetRiderRank ? (
            <DropdownMenuItem onClick={() => setDialog("rider-rank")}>
              <MedalIcon weight="fill" />
              Change Rank
            </DropdownMenuItem>
          ) : null}
          {/* TSESSION-012…015 — the weekly securing minimum, Monday Priority
              Opening and Long-Distance eligibility. Superadmin only (OQ-80). */}
          {access.canSetTherapistRank ? (
            <DropdownMenuItem onClick={() => setDialog("therapist-rank")}>
              <MedalIcon weight="fill" />
              Change Rank
            </DropdownMenuItem>
          ) : null}
          {access.canSetPlate ? (
            <DropdownMenuItem onClick={() => setDialog("plate")}>
              <IdentificationCardIcon weight="fill" />
              {vehicle ? "Change plate" : "Set plate"}
            </DropdownMenuItem>
          ) : null}
          {access.canReassignHub ? (
            <DropdownMenuItem onClick={() => setDialog("hub")}>
              <BriefcaseIcon weight="fill" />
              Reassign Hub
            </DropdownMenuItem>
          ) : null}
          {/* Lost or changed phone: the SMS-code reset cannot reach them. */}
          {access.canResetPassword ? (
            <DropdownMenuItem onClick={() => setDialog("reset-password")}>
              <KeyIcon weight="fill" />
              Reset password
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <StaffEditDialogs
        dialog={dialog}
        onDialogChange={setDialog}
        person={person}
        vehicle={vehicle}
      />
    </>
  );
}
